import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc, writeBatch } from "firebase/firestore";
import Seite from "../components/Seite";

const SAMMLUNG = "health";

// Erstaufnahme: Einzelwerte aus der BlutdruckDaten-Auswertung 19.07.2026–17.10.2026
const ERSTIMPORT = [
  { datum: "2026-08-10T21:11", sys: 148, dia: 90, puls: 86, gewicht: 88.0, messort: "OL", kommentar: "" },
  { datum: "2026-08-10T21:15", sys: 138, dia: 87, puls: 86, gewicht: 88.0, messort: "OL", kommentar: "" },
  { datum: "2026-09-05T09:14", sys: 150, dia: 95, puls: 80, gewicht: 88.0, messort: "OL", kommentar: "" },
  { datum: "2026-09-06T09:16", sys: 158, dia: 98, puls: 88, gewicht: 88.0, messort: "OL", kommentar: "" },
  { datum: "2026-10-12T08:43", sys: 130, dia: 80, puls: null, gewicht: 88.0, messort: "OL", kommentar: "Beim Arzt", bdArt: "arzt" },
].map((e) => ({ bdArt: "eigenes_geraet", gewichtArt: "", ...e }));

const MESSORTE = { OL: "Oberarm links", OR: "Oberarm rechts", HL: "Handgelenk links", HR: "Handgelenk rechts" };

const BD_ARTEN = { eigenes_geraet: "Eigenes Gerät", uhr: "Uhr", arzt: "Arzt" };
const GEWICHT_ARTEN = { eigene_waage: "Eigene Waage", geschaetzt: "Geschätzt", fremdwaage: "Fremdwaage", arzt: "Arzt" };

const ZEITRAEUME = [
  { key: 30, label: "30 Tage" },
  { key: 90, label: "90 Tage" },
  { key: 365, label: "1 Jahr" },
  { key: 0, label: "Alle" },
];

// Einstufung nach der Normalwerttabelle aus der BlutdruckDaten-Auswertung
function einstufung(sys, dia) {
  if (sys == null || dia == null) return null;
  if (sys >= 180 || dia >= 110) return { label: "Hypertonie Grad 3", farbe: "#b71c1c" };
  if (sys >= 160 || dia >= 100) return { label: "Hypertonie Grad 2", farbe: "#e53935" };
  if (sys >= 140 || dia >= 90) return { label: "Hypertonie Grad 1", farbe: "#fb8c00" };
  if (sys >= 130 || dia >= 85) return { label: "hochnormal", farbe: "#fbc02d" };
  if (sys >= 120 || dia >= 80) return { label: "normal", farbe: "#7cb342" };
  return { label: "optimal", farbe: "#34a853" };
}

function jetztLokal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function datumAnzeige(iso) {
  const [d, t] = iso.split("T");
  const [j, m, tag] = d.split("-");
  return `${tag}.${m}.${j} ${t || ""}`.trim();
}

const zahl = (v) => (v === "" || v == null || isNaN(Number(String(v).replace(",", "."))) ? null : Number(String(v).replace(",", ".")));
const mittel = (werte) => (werte.length ? Math.round((werte.reduce((a, b) => a + b, 0) / werte.length) * 10) / 10 : null);

// --- Samsung-Health-CSV-Export ---------------------------------------------------------------
// Samsung Health → Einstellungen → "Persönliche Daten herunterladen" liefert je Datentyp eine
// CSV (z.B. com.samsung.shealth.blood_pressure.*.csv, com.samsung.health.weight.*.csv).
// Zeile 1 ist eine Metazeile, danach folgt die Kopfzeile; Spaltennamen tragen teils ein Präfix.
function csvZeile(zeile) {
  const out = [];
  let feld = "", inQuote = false;
  for (let i = 0; i < zeile.length; i++) {
    const c = zeile[i];
    if (c === '"') inQuote = !inQuote;
    else if (c === "," && !inQuote) { out.push(feld); feld = ""; }
    else feld += c;
  }
  out.push(feld);
  return out.map((f) => f.trim());
}

function samsungZeit(start, offset) {
  // start_time ist UTC ("2026-10-12 06:43:00.000"), time_offset z.B. "UTC+0200"
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(start || "");
  if (!m) return null;
  let ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const o = /([+-])(\d{2})(\d{2})/.exec(offset || "");
  if (o) ms += (o[1] === "-" ? -1 : 1) * ((+o[2]) * 60 + (+o[3])) * 60000;
  else ms -= new Date(ms).getTimezoneOffset() * 60000;
  return new Date(ms).toISOString().slice(0, 16);
}

function samsungCsvParsen(text) {
  const zeilen = text.split(/\r?\n/).filter((z) => z.trim());
  const kopfIndex = zeilen.findIndex((z) => /start_time/.test(z) && /(systolic|weight)/.test(z));
  if (kopfIndex < 0) return [];
  const kopf = csvZeile(zeilen[kopfIndex]);
  const spalte = (name) => kopf.findIndex((k) => k === name || k.endsWith("." + name));
  const s = { sys: spalte("systolic"), dia: spalte("diastolic"), puls: spalte("pulse"), gewicht: spalte("weight"), start: spalte("start_time"), offset: spalte("time_offset"), kommentar: spalte("comment") };
  const ergebnis = [];
  for (const z of zeilen.slice(kopfIndex + 1)) {
    const f = csvZeile(z);
    const datum = samsungZeit(f[s.start], s.offset >= 0 ? f[s.offset] : "");
    if (!datum) continue;
    const eintrag = {
      datum,
      sys: s.sys >= 0 ? zahl(f[s.sys]) : null,
      dia: s.dia >= 0 ? zahl(f[s.dia]) : null,
      puls: s.puls >= 0 ? zahl(f[s.puls]) : null,
      gewicht: s.gewicht >= 0 ? zahl(f[s.gewicht]) : null,
      messort: "",
      kommentar: s.kommentar >= 0 ? f[s.kommentar] || "" : "",
    };
    eintrag.bdArt = eintrag.sys != null ? "uhr" : "";
    eintrag.gewichtArt = "";
    if ((eintrag.sys && eintrag.dia) || eintrag.gewicht) ergebnis.push(eintrag);
  }
  return ergebnis;
}

// --- Verlaufsdiagramm ------------------------------------------------------------------------
function Verlauf({ eintraege, serien, referenz = [], einheit }) {
  const B = 640, H = 200, L = 36, R = 10, T = 10, U = 24;
  const punkte = eintraege.filter((e) => serien.some((s) => e[s.feld] != null));
  if (punkte.length === 0) return <p style={styles.leer}>Keine Werte im Zeitraum.</p>;
  const zeiten = punkte.map((e) => new Date(e.datum).getTime());
  const tMin = Math.min(...zeiten), tMax = Math.max(...zeiten);
  const werte = punkte.flatMap((e) => serien.map((s) => e[s.feld]).filter((v) => v != null)).concat(referenz.map((r) => r.wert));
  const vMin = Math.floor((Math.min(...werte) - 5) / 5) * 5, vMax = Math.ceil((Math.max(...werte) + 5) / 5) * 5;
  const x = (t) => (tMax === tMin ? (L + B - R) / 2 : L + ((t - tMin) / (tMax - tMin)) * (B - L - R));
  const y = (v) => T + (1 - (v - vMin) / (vMax - vMin)) * (H - T - U);
  const ticks = [];
  for (let v = vMin; v <= vMax; v += Math.max(5, Math.round((vMax - vMin) / 4 / 5) * 5)) ticks.push(v);
  const datumKurz = (t) => { const d = new Date(t); return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`; };

  return (
    <div>
      <svg viewBox={`0 0 ${B} ${H}`} style={{ width: "100%", height: "auto" }}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={B - R} y1={y(v)} y2={y(v)} stroke="#eee" />
            <text x={L - 6} y={y(v) + 4} fontSize="10" textAnchor="end" fill="#888">{v}</text>
          </g>
        ))}
        {referenz.map((r) => (
          <line key={r.wert} x1={L} x2={B - R} y1={y(r.wert)} y2={y(r.wert)} stroke={r.farbe} strokeDasharray="4 4" />
        ))}
        <text x={L} y={H - 6} fontSize="10" fill="#888">{datumKurz(tMin)}</text>
        <text x={B - R} y={H - 6} fontSize="10" fill="#888" textAnchor="end">{datumKurz(tMax)}</text>
        {serien.map((s) => {
          const ps = punkte.filter((e) => e[s.feld] != null);
          return (
            <g key={s.feld}>
              <polyline fill="none" stroke={s.farbe} strokeWidth="2" points={ps.map((e) => `${x(new Date(e.datum).getTime())},${y(e[s.feld])}`).join(" ")} />
              {ps.map((e) => (
                <circle key={e.id} cx={x(new Date(e.datum).getTime())} cy={y(e[s.feld])} r="3.5" fill={s.farbe}>
                  <title>{`${datumAnzeige(e.datum)}: ${e[s.feld]} ${einheit}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={styles.legende}>
        {serien.map((s) => <span key={s.feld}><span style={{ ...styles.legendePunkt, backgroundColor: s.farbe }} />{s.label}</span>)}
        {referenz.map((r) => <span key={r.wert} style={{ color: "#888" }}>– – {r.label}</span>)}
      </div>
    </div>
  );
}

const LEER = { datum: "", sys: "", dia: "", puls: "", gewicht: "", bauchumfang: "", messort: "OL", bdArt: "eigenes_geraet", gewichtArt: "eigene_waage", kommentar: "" };

const TYPEN = [
  { key: "blutdruck", label: "🩺 Blutdruck", hat: (e) => e.sys != null && e.dia != null },
  { key: "gewicht", label: "⚖️ Gewicht", hat: (e) => e.gewicht != null },
  { key: "bauch", label: "📏 Bauchumfang", hat: (e) => e.bauchumfang != null },
];
const LEERE_WERTE = { sys: null, dia: null, puls: null, messort: "", bdArt: "", gewicht: null, gewichtArt: "", bauchumfang: null };
const kommaZahl = (v, stellen = 1) => v.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });

export default function Health({ user }) {
  const [eintraege, setEintraege] = useState([]);
  const [form, setForm] = useState({ ...LEER, datum: jetztLokal() });
  const [editId, setEditId] = useState(null);
  // Angezeigte Eingabebereiche: neu immer genau einer, beim Bearbeiten alle Werte, die der Eintrag enthält
  const [typen, setTypen] = useState(["blutdruck"]);
  const [zeitraum, setZeitraum] = useState(90);
  const [meldung, setMeldung] = useState("");

  useEffect(() => {
    const q = query(collection(db, SAMMLUNG), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => a.datum.localeCompare(b.datum));
      setEintraege(data);
    }, (err) => setMeldung("Fehler beim Laden: " + err.message));
  }, [user.uid]);

  const imZeitraum = useMemo(() => {
    if (!zeitraum) return eintraege;
    const grenze = new Date(Date.now() - zeitraum * 86400000).toISOString().slice(0, 16);
    return eintraege.filter((e) => e.datum >= grenze);
  }, [eintraege, zeitraum]);

  const bd = imZeitraum.filter((e) => e.sys != null && e.dia != null);
  const gw = imZeitraum.filter((e) => e.gewicht != null);
  const letzterBd = [...eintraege].reverse().find((e) => e.sys != null && e.dia != null);
  const letztesGw = [...eintraege].reverse().find((e) => e.gewicht != null);
  const bu = imZeitraum.filter((e) => e.bauchumfang != null);
  const letzterBauch = [...eintraege].reverse().find((e) => e.bauchumfang != null);
  const schnittSys = mittel(bd.map((e) => e.sys)), schnittDia = mittel(bd.map((e) => e.dia));
  const schnittPuls = mittel(bd.filter((e) => e.puls != null).map((e) => e.puls));

  const vorhandeneSchluessel = useMemo(() => new Set(eintraege.map((e) => `${e.datum}|${e.sys ?? ""}|${e.gewicht ?? ""}`)), [eintraege]);
  const schluessel = (e) => `${e.datum}|${e.sys ?? ""}|${e.gewicht ?? ""}`;
  const erstimportOffen = ERSTIMPORT.filter((e) => !vorhandeneSchluessel.has(schluessel(e)));

  const importieren = async (liste, quelle) => {
    const neu = liste.filter((e) => !vorhandeneSchluessel.has(schluessel(e)));
    for (let i = 0; i < neu.length; i += 400) {
      const batch = writeBatch(db);
      neu.slice(i, i + 400).forEach((e) => batch.set(doc(collection(db, SAMMLUNG)), { ...e, quelle, uid: user.uid, erstellt: new Date() }));
      await batch.commit();
    }
    setMeldung(`${neu.length} Messung(en) importiert, ${liste.length - neu.length} bereits vorhanden.`);
  };

  const csvImport = async (ev) => {
    const dateien = [...ev.target.files];
    ev.target.value = "";
    let alle = [];
    for (const f of dateien) alle = alle.concat(samsungCsvParsen(await f.text()));
    if (alle.length === 0) { setMeldung("In der Datei wurden keine Blutdruck- oder Gewichtswerte erkannt."); return; }
    await importieren(alle, "Samsung Health");
  };

  const speichern = async (ev) => {
    ev.preventDefault();
    const daten = { datum: form.datum || jetztLokal(), kommentar: form.kommentar.trim() };
    if (typen.includes("blutdruck")) {
      Object.assign(daten, { sys: zahl(form.sys), dia: zahl(form.dia), puls: zahl(form.puls), messort: form.messort, bdArt: form.bdArt });
      if (!daten.sys || !daten.dia) { setMeldung("Bitte systolischen und diastolischen Wert eintragen."); return; }
    }
    if (typen.includes("gewicht")) {
      Object.assign(daten, { gewicht: zahl(form.gewicht), gewichtArt: form.gewichtArt });
      if (!daten.gewicht) { setMeldung("Bitte das Gewicht eintragen."); return; }
    }
    if (typen.includes("bauch")) {
      daten.bauchumfang = zahl(form.bauchumfang);
      if (!daten.bauchumfang) { setMeldung("Bitte den Bauchumfang eintragen."); return; }
    }
    if (editId) await updateDoc(doc(db, SAMMLUNG, editId), daten);
    else await addDoc(collection(db, SAMMLUNG), { ...LEERE_WERTE, ...daten, quelle: "manuell", uid: user.uid, erstellt: new Date() });
    abbrechen();
    setMeldung("");
  };

  const abbrechen = () => {
    setEditId(null);
    setTypen((t) => (t.length === 1 ? t : ["blutdruck"]));
    setForm({ ...LEER, datum: jetztLokal() });
  };

  const bearbeiten = (e) => {
    setEditId(e.id);
    setTypen(TYPEN.filter((t) => t.hat(e)).map((t) => t.key));
    setForm({
      datum: e.datum, sys: e.sys ?? "", dia: e.dia ?? "", puls: e.puls ?? "", gewicht: e.gewicht ?? "", bauchumfang: e.bauchumfang ?? "",
      messort: e.messort || "", bdArt: e.bdArt || "", gewichtArt: e.gewichtArt || "", kommentar: e.kommentar || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loeschen = async (e) => {
    if (window.confirm(`Messung vom ${datumAnzeige(e.datum)} löschen?`)) await deleteDoc(doc(db, SAMMLUNG, e.id));
  };

  const feld = (name) => ({ value: form[name], onChange: (ev) => setForm({ ...form, [name]: ev.target.value }) });
  const stufeLetzter = letzterBd && einstufung(letzterBd.sys, letzterBd.dia);
  const stufeSchnitt = einstufung(schnittSys, schnittDia);

  return (
    <Seite titel="Health">
      <div style={styles.kacheln}>
        <div style={styles.kachel}>
          <div style={styles.kachelLabel}>Letzter Blutdruck</div>
          <div style={styles.kachelWert}>{letzterBd ? `${letzterBd.sys}/${letzterBd.dia}` : "–"}<span style={styles.einheit}> mmHg</span></div>
          {stufeLetzter && <span style={{ ...styles.badge, backgroundColor: stufeLetzter.farbe }}>{stufeLetzter.label}</span>}
          {letzterBd && <div style={styles.kachelSub}>{datumAnzeige(letzterBd.datum)}</div>}
        </div>
        <div style={styles.kachel}>
          <div style={styles.kachelLabel}>Ø Blutdruck ({ZEITRAEUME.find((z) => z.key === zeitraum).label})</div>
          <div style={styles.kachelWert}>{schnittSys ? `${Math.round(schnittSys)}/${Math.round(schnittDia)}` : "–"}<span style={styles.einheit}> mmHg</span></div>
          {stufeSchnitt && <span style={{ ...styles.badge, backgroundColor: stufeSchnitt.farbe }}>{stufeSchnitt.label}</span>}
          <div style={styles.kachelSub}>{bd.length} Messungen{schnittPuls ? ` · Ø Puls ${Math.round(schnittPuls)}` : ""}</div>
        </div>
        <div style={styles.kachel}>
          <div style={styles.kachelLabel}>Gewicht</div>
          <div style={styles.kachelWert}>{letztesGw ? letztesGw.gewicht.toLocaleString("de-DE", { minimumFractionDigits: 1 }) : "–"}<span style={styles.einheit}> kg</span></div>
          {letztesGw && <div style={styles.kachelSub}>{datumAnzeige(letztesGw.datum)}</div>}
        </div>
        <div style={styles.kachel}>
          <div style={styles.kachelLabel}>Bauchumfang</div>
          <div style={styles.kachelWert}>{letzterBauch ? kommaZahl(letzterBauch.bauchumfang) : "–"}<span style={styles.einheit}> cm</span></div>
          {letzterBauch && <div style={styles.kachelSub}>{datumAnzeige(letzterBauch.datum)}</div>}
        </div>
      </div>

      <form onSubmit={speichern} style={styles.karte}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.7rem", flexWrap: "wrap" }}>
          <span style={{ ...styles.karteTitel, marginBottom: 0, marginRight: "auto" }}>{editId ? "Messung bearbeiten" : "Neue Messung"}</span>
          {typen.length === 1 && TYPEN.map((m) => (
            <button key={m.key} type="button" disabled={!!editId}
              style={{ ...styles.filterBtn, backgroundColor: typen[0] === m.key ? "#1a3558" : "#dde3ec", color: typen[0] === m.key ? "white" : "#333", opacity: editId && typen[0] !== m.key ? 0.4 : 1 }}
              onClick={() => setTypen([m.key])}>
              {m.label}
            </button>
          ))}
        </div>
        <div style={styles.formGrid}>
          <label style={styles.label}>Datum/Uhrzeit<input style={styles.input} type="datetime-local" {...feld("datum")} /></label>
          {typen.includes("blutdruck") && <>
            <label style={styles.label}>Systolisch<input style={styles.input} inputMode="numeric" placeholder="mmHg" {...feld("sys")} /></label>
            <label style={styles.label}>Diastolisch<input style={styles.input} inputMode="numeric" placeholder="mmHg" {...feld("dia")} /></label>
            <label style={styles.label}>Puls<input style={styles.input} inputMode="numeric" placeholder="/min" {...feld("puls")} /></label>
            <label style={styles.label}>Gemessen mit
              <select style={styles.input} {...feld("bdArt")}>
                {!form.bdArt && <option value="">–</option>}
                {Object.entries(BD_ARTEN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label style={styles.label}>Messort
              <select style={styles.input} {...feld("messort")}>
                <option value="">–</option>
                {Object.entries(MESSORTE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </>}
          {typen.includes("gewicht") && <>
            <label style={styles.label}>Gewicht<input style={styles.input} inputMode="decimal" placeholder="kg" {...feld("gewicht")} /></label>
            <label style={styles.label}>Gewogen mit
              <select style={styles.input} {...feld("gewichtArt")}>
                {!form.gewichtArt && <option value="">–</option>}
                {Object.entries(GEWICHT_ARTEN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </>}
          {typen.includes("bauch") && (
            <label style={styles.label}>Bauchumfang<input style={styles.input} inputMode="decimal" placeholder="cm" {...feld("bauchumfang")} /></label>
          )}
        </div>
        <input style={{ ...styles.input, width: "100%", boxSizing: "border-box", marginTop: "0.5rem" }} placeholder="Kommentar (z.B. beim Arzt, nach Sport)" {...feld("kommentar")} />
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.7rem" }}>
          <button style={styles.button} type="submit">{editId ? "Speichern" : "Hinzufügen"}</button>
          {editId && <button style={styles.buttonGrau} type="button" onClick={abbrechen}>Abbrechen</button>}
        </div>
      </form>

      {meldung && <div style={styles.meldung} onClick={() => setMeldung("")}>{meldung}</div>}

      <div style={styles.filterBar}>
        {ZEITRAEUME.map((z) => (
          <button key={z.key}
            style={{ ...styles.filterBtn, backgroundColor: zeitraum === z.key ? "#1a3558" : "#dde3ec", color: zeitraum === z.key ? "white" : "#333" }}
            onClick={() => setZeitraum(z.key)}>
            {z.label}
          </button>
        ))}
      </div>

      <div style={styles.karte}>
        <div style={styles.karteTitel}>Blutdruck & Puls</div>
        <Verlauf eintraege={bd} einheit="" serien={[
          { feld: "sys", label: "Systolisch", farbe: "#e53935" },
          { feld: "dia", label: "Diastolisch", farbe: "#1a73e8" },
          { feld: "puls", label: "Puls", farbe: "#9e9e9e" },
        ]} referenz={[{ wert: 135, label: "135 (Grenze Heimmessung)", farbe: "#e5393588" }, { wert: 85, label: "85", farbe: "#1a73e888" }]} />
      </div>

      <div style={styles.karte}>
        <div style={styles.karteTitel}>Gewicht</div>
        <Verlauf eintraege={gw} einheit="kg" serien={[{ feld: "gewicht", label: "Gewicht (kg)", farbe: "#34a853" }]} />
      </div>

      <div style={styles.karte}>
        <div style={styles.karteTitel}>Bauchumfang</div>
        <Verlauf eintraege={bu} einheit="cm" serien={[{ feld: "bauchumfang", label: "Bauchumfang (cm)", farbe: "#8e44ad" }]}
          referenz={[{ wert: 94, label: "94 (erhöht)", farbe: "#fb8c0088" }, { wert: 102, label: "102 (deutlich erhöht)", farbe: "#e5393588" }]} />
      </div>

      <div style={styles.karte}>
        <div style={styles.karteTitel}>Messungen ({imZeitraum.length})</div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.tabelle}>
            <thead>
              <tr>{["Datum", "Blutdruck", "Puls", "Gerät / Ort", "Gewicht", "Waage", "Bauch", "Kommentar", ""].map((h) => <th key={h} style={styles.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {[...imZeitraum].reverse().map((e) => {
                const st = einstufung(e.sys, e.dia);
                return (
                  <tr key={e.id}>
                    <td style={styles.td}>{datumAnzeige(e.datum)}</td>
                    <td style={{ ...styles.td, fontWeight: "bold", color: st ? st.farbe : "inherit", whiteSpace: "nowrap" }} title={st ? st.label : ""}>{st ? `${e.sys}/${e.dia}` : ""}</td>
                    <td style={styles.td}>{e.puls ?? ""}</td>
                    <td style={styles.td} title={MESSORTE[e.messort] || ""}>{st ? [BD_ARTEN[e.bdArt], e.messort].filter(Boolean).join(" · ") : ""}</td>
                    <td style={styles.td}>{e.gewicht != null ? e.gewicht.toLocaleString("de-DE", { minimumFractionDigits: 1 }) : ""}</td>
                    <td style={styles.td}>{e.gewicht != null ? GEWICHT_ARTEN[e.gewichtArt] || "" : ""}</td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{e.bauchumfang != null ? `${kommaZahl(e.bauchumfang)} cm` : ""}</td>
                    <td style={styles.td} title={e.quelle ? `Quelle: ${e.quelle}` : ""}>{e.kommentar || ""}</td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                      <button style={styles.iconBtn} onClick={() => bearbeiten(e)}>✏️</button>
                      <button style={styles.iconBtn} onClick={() => loeschen(e)}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {imZeitraum.length === 0 && <p style={styles.leer}>Keine Messungen im Zeitraum.</p>}
      </div>

      <div style={styles.karte}>
        <div style={styles.karteTitel}>Import</div>
        {erstimportOffen.length > 0 && (
          <div style={styles.importZeile}>
            <span style={{ flex: 1 }}>Erstaufnahme aus BlutdruckDaten (19.07.–17.10.2026): {erstimportOffen.length} Messungen</span>
            <button style={styles.button} onClick={() => importieren(ERSTIMPORT, "BlutdruckDaten")}>Übernehmen</button>
          </div>
        )}
        <div style={styles.importZeile}>
          <span style={{ flex: 1 }}>
            Samsung-Health-Export (CSV): In Samsung Health → Einstellungen → „Persönliche Daten herunterladen“,
            dann die Dateien <code>…blood_pressure…csv</code> und <code>…weight…csv</code> wählen. Doppelte Werte werden übersprungen.
          </span>
          <label style={{ ...styles.button, display: "inline-block" }}>
            CSV wählen
            <input type="file" accept=".csv,text/csv" multiple style={{ display: "none" }} onChange={csvImport} />
          </label>
        </div>
      </div>
    </Seite>
  );
}

const styles = {
  kacheln: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.8rem", marginBottom: "1rem" },
  kachel: { backgroundColor: "white", borderRadius: "6px", padding: "0.9rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  kachelLabel: { fontSize: "0.8rem", color: "#666", marginBottom: "0.3rem" },
  kachelWert: { fontSize: "1.6rem", fontWeight: "bold", color: "#1a3558" },
  kachelSub: { fontSize: "0.75rem", color: "#888", marginTop: "0.3rem" },
  einheit: { fontSize: "0.8rem", fontWeight: "normal", color: "#888" },
  badge: { display: "inline-block", fontSize: "0.7rem", color: "white", padding: "0.15rem 0.5rem", borderRadius: "10px", marginTop: "0.3rem" },
  karte: { backgroundColor: "white", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  karteTitel: { fontWeight: "bold", marginBottom: "0.7rem", color: "#1a3558" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "0.5rem" },
  label: { display: "flex", flexDirection: "column", fontSize: "0.8rem", color: "#555", gap: "0.2rem" },
  input: { padding: "0.5rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px", minWidth: 0 },
  button: { padding: "0.6rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.9rem", whiteSpace: "nowrap" },
  buttonGrau: { padding: "0.6rem 1rem", backgroundColor: "#dde3ec", color: "#333", border: "none", borderRadius: "4px", cursor: "pointer" },
  meldung: { backgroundColor: "#e8f0fe", color: "#1a3558", padding: "0.6rem 0.8rem", borderRadius: "4px", marginBottom: "1rem", cursor: "pointer", fontSize: "0.9rem" },
  filterBar: { display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" },
  filterBtn: { padding: "0.4rem 0.8rem", border: "none", borderRadius: "20px", cursor: "pointer", fontSize: "0.9rem" },
  legende: { display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.75rem", color: "#555", marginTop: "0.3rem" },
  legendePunkt: { display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", marginRight: "4px", verticalAlign: "middle" },
  tabelle: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "0.4rem", borderBottom: "2px solid #eee", color: "#666", fontWeight: "600" },
  td: { padding: "0.4rem", borderBottom: "1px solid #f5f5f5" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  importZeile: { display: "flex", gap: "0.8rem", alignItems: "center", padding: "0.5rem 0", borderBottom: "1px solid #f5f5f5", fontSize: "0.85rem", flexWrap: "wrap" },
  leer: { color: "#999", textAlign: "center" },
};
